import React, {useCallback, useEffect, useMemo, useState} from "react";
import { ColumnsType } from "antd/es/table";
import { Button, Input, message, Space, Table } from "antd";
import {numberValidator} from "../../../../utils/Validator";
import {WaterData} from "../../../../types/WaterData";
import {
    addWaterRow,
    deleteWaterByWid,
    fetchWaterByPid,
    updateWaterRow
} from "../../../../backendInteraction/WaterApiFetch";

interface WaterTableProps {
    data: WaterData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
}

const WaterTable: React.FC<WaterTableProps> = ( {data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow })=> {
    const [waterData, setWaterData] = useState<WaterData[]>([]);
    const [editingWid, setEditingWid] = useState<number | null>(null);
    const [editData, setEditData] = useState<WaterData | null>(null);

    useEffect(() => {
        setWaterData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingWid !== -1 ) {
            const newRow: WaterData = {
                key: -1,
                wid: -1,
                loads: "",
                kilometres: "",
                litres: "",
                hours: "",
                driver: "",
                pid: plodId ?? -1,
            }
            setWaterData(prevData => {
                const hasNewRow = prevData.some(item => item.wid === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            })

            setEditingWid(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingWid]);


    useEffect(() => {
        if (isView) {
            if (editingWid === -1) {
                setWaterData(prevData => prevData.filter(item => item.wid !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingWid(null);
            setEditData(null);
        }
    }, [isView])


    const saveChanges = useCallback( async () => {
        console.log("Saving changes:", editData);

        if(!editData) return;

        const { loads, kilometres, litres, hours } = editData;

        if(!!loads) {
            const isNumber = numberValidator(loads, 'Loads');
            if (!isNumber) return;
            if (parseFloat(loads) < 0) {
                message.error('The "Loads" value cannot be less than 0.');
                return;
            }
        }

        if(!!kilometres) {
            const isNumber = numberValidator(kilometres, 'Kilometres');
            if (!isNumber) return;
            if (parseFloat(kilometres) < 0) {
                message.error('The "Kilometres" value cannot be less than 0.');
                return;
            }
        }

        if(!!litres) {
            const isNumber = numberValidator(litres, 'Litres');
            if (!isNumber) return;
            if (parseFloat(litres) < 0) {
                message.error('The "Litres" value cannot be less than 0.');
                return;
            }
        }

        if(!!hours) {
            const isNumber = numberValidator(hours, 'Hours');
            if (!isNumber) return;
            const hoursVal = parseFloat(hours);
            if (hoursVal < 0 || hoursVal > 24) {
                message.error('The "Hours" value is invalid. Please enter a number between 0 and 24.');
                return;
            }
        }

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingWid === -1) {
            const success = await addWaterRow(editData, aid);
            if (success) {
                setWaterData( await fetchWaterByPid(plodId ?? -1, aid));
                message.success("New water added successfully!");
            } else {
                message.error("Failed to add new water.");
            }
        } else {
            const success = await updateWaterRow(editingWid ?? -1, editData);
            if (success) {
                setWaterData( await fetchWaterByPid(plodId ?? -1, aid));
                message.success("Water updated successfully!");
            } else {
                message.error("Failed to update water.");
            }
        }

        if (editingWid === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingWid(null);
        setEditData(null);
    }, [editData, editingWid,setIsAddNewRow]);

    const cancelEditing = useCallback((record: WaterData) => {
        console.log("Cancel editing");
        setEditingWid(null);
        setEditData(null);

        if (record.wid === -1) {
            setWaterData(prevData => prevData.filter(item => item.wid !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: WaterData) => {
        console.log("Editing row :", record);
        if ( editingWid === -1 ) {
            setWaterData(prevData => prevData.filter(item => item.wid !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingWid( prevId => {
            if (prevId !== null) cancelEditing({wid: prevId} as WaterData);
            return record.wid;
        });

        setEditData({ ...record }) ;
    }, [editingWid, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: WaterData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteWaterByWid(record.wid);
        if (success) {
            setWaterData(await fetchWaterByPid(plodId ?? -1, aid));
            message.success("Water deleted successfully!");
        } else {
            message.error("Failed to delete water.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof WaterData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);


    const columns: ColumnsType<WaterData> = useMemo(() => [
        {
            title: "Loads",
            dataIndex: "loads",
            key: "loads",
            width: "16%",
            render: (_, record) =>
                editingWid === record.wid ? (
                    <Input
                        value={editData?.loads}
                        onChange={(e) => handleInputChange("loads", e.target.value)}
                    />
                ) : (
                    record.loads
                ),
        },
        {
            title: 'Kilometres',
            dataIndex: 'kilometres',
            key: 'kilometres',
            width: "16%",
            render: (_, record) =>
                editingWid === record.wid ? (
                    <Input
                        value={editData?.kilometres}
                        onChange={(e) => handleInputChange("kilometres", e.target.value)}
                    />
                ) : (
                    record.kilometres
                ),
        },
        {
            title: 'Litres',
            dataIndex: 'litres',
            key: 'litres',
            width: "16%",
            render: (_, record) =>
                editingWid === record.wid ? (
                    <Input
                        value={editData?.litres}
                        onChange={(e) => handleInputChange("litres", e.target.value)}
                    />
                ) : (
                    record.litres
                ),
        },
        {
            title: 'Hours',
            dataIndex: 'hours',
            key: 'hours',
            width: "16%",
            render: (_, record) =>
                editingWid === record.wid ? (
                    <Input
                        value={editData?.hours}
                        onChange={(e) => handleInputChange("hours", e.target.value)}
                    />
                ) : (
                    record.hours
                ),
        },
        {
            title: 'Driver',
            dataIndex: 'driver',
            key: 'driver',
            width: "16%",
            render: (_, record) =>
                editingWid === record.wid ? (
                    <Input
                        value={editData?.driver}
                        onChange={(e) => handleInputChange("driver", e.target.value)}
                    />
                ) : (
                    record.driver
                ),
        },
        ...(!isView
                ? [{
                    title: 'Action',
                    key: 'action',
                    width: "20%",
                    render: (_: any , record: WaterData) => (
                        <Space size="small">
                            <Space size="small">
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingWid === record.wid ? saveChanges() : startEditing(record))}>
                                    {editingWid === record.wid ? "Save" : "Edit"}
                                </Button>
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingWid === record.wid ? cancelEditing(record) : deleteRow(record))}>
                                    {editingWid === record.wid ? "Cancel" : "Delete"}
                                </Button>
                            </Space>
                        </Space>
                    ),
                }] as ColumnsType<WaterData>
                : []
        ),
    ], [isView, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<WaterData>
            columns={columns}
            pagination={false}
            dataSource={waterData}
        />
    );
};

export default WaterTable;